/**
 * New Ticket Page
 * Form for creating a new ticket with asset linking and priority selection.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ticketApi, assetApi } from '../api/client';
import { ArrowLeft, AlertTriangle } from 'lucide-react';

const categories = [
  'Hardware', 'Software', 'Network', 'Security',
  'Radio', 'Dispatch', 'CAD/RMS', 'Infrastructure', 'Other',
];

export default function NewTicketPage() {
  const navigate = useNavigate();
  const { isSentinelUser } = useAuth();
  const basePath = isSentinelUser ? '/sentinel' : '/portal';

  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: '',
    asset_id: '',
  });
  const [assets, setAssets] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    async function loadAssets() {
      try {
        const res = await assetApi.list({ page_size: 100 });
        setAssets(res.data.items || res.data || []);
      } catch (err) {
        console.error('Failed to load assets:', err);
      }
    }
    loadAssets();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const payload = {
        ...form,
        asset_id: form.asset_id || null,
      };
      const res = await ticketApi.create(payload);
      navigate(`${basePath}/tickets/${res.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  }

  const update = (field, value) => setForm({ ...form, [field]: value });

  return (
    <div className="p-8 max-w-3xl">
      <button
        onClick={() => navigate(`${basePath}/tickets`)}
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-300 mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to tickets
      </button>

      <h1 className="text-xl font-semibold text-white mb-6">New ticket</h1>

      {error && (
        <div className="flex items-center gap-2 mb-4 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1.5">Title</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => update('title', e.target.value)}
            placeholder="Brief description of the issue"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-600"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1.5">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            placeholder="Provide details about the issue, including any error messages, steps to reproduce, and impact..."
            rows={5}
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-gray-600 resize-none"
          />
        </div>

        {/* Priority + Category row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1.5">Priority</label>
            <select
              value={form.priority}
              onChange={(e) => update('priority', e.target.value)}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 focus:outline-none focus:border-gray-600"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1.5">Category</label>
            <select
              value={form.category}
              onChange={(e) => update('category', e.target.value)}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 focus:outline-none focus:border-gray-600"
            >
              <option value="">Select category</option>
              {categories.map((c) => (
                <option key={c} value={c.toLowerCase()}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Linked asset */}
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-1.5">
            Linked asset <span className="text-gray-600">(optional)</span>
          </label>
          <select
            value={form.asset_id}
            onChange={(e) => update('asset_id', e.target.value)}
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-200 focus:outline-none focus:border-gray-600"
          >
            <option value="">No asset linked</option>
            {assets.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.asset_type}) — {a.criticality}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-600 mt-1">
            Linking an asset automatically factors its criticality into the risk score.
          </p>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-800">
          <button
            type="button"
            onClick={() => navigate(`${basePath}/tickets`)}
            className="px-4 py-2.5 text-sm text-gray-400 hover:text-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {submitting ? 'Creating...' : 'Create ticket'}
          </button>
        </div>
      </form>
    </div>
  );
}
